/** A semana de funcionamento — sete dias, cada um com suas faixas.
 *
 * Nasceu dentro do assistente de configuração. Virou componente quando o
 * painel passou a editar o mesmo horário depois da oficina já aberta: são
 * as mesmas cinco funções de `map` aninhado, e duas cópias divergem — a
 * do assistente ganharia o intervalo de almoço que a de configurações não
 * teria, e o dono descobriria isso tentando fechar para o almoço.
 *
 * O componente não guarda estado nem sabe salvar. Quem usa decide o que
 * fazer com a semana: o assistente manda e avança de passo, a tela de
 * configurações mostra um botão de salvar e o aviso de "salvo".
 */

import type { DiaHorario, Faixa } from "@/lib/horarios";
import { useFormat, useT } from "@/lib/i18n";

/** A faixa da tarde, quando o dono separa o almoço. */
const TARDE: Faixa = ["13:00", "19:00"];
/** Manhã, tarde e noite dão conta de qualquer oficina; a quarta faixa só
 *  serve para transformar a tela numa planilha. */
const MAX_FAIXAS = 3;

export default function WeekHours({
  semana,
  onChange,
  disabled = false,
}: {
  semana: DiaHorario[];
  onChange: (semana: DiaHorario[]) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const { weekday } = useFormat();

  /** Troca um dia da semana, mantendo os outros seis intactos. */
  const mexer = (indice: number, muda: (dia: DiaHorario) => DiaHorario) =>
    onChange(semana.map((d, i) => (i === indice ? muda(d) : d)));

  /** Troca uma ponta de uma faixa: `mexer` para dentro do dia. */
  const mexerFaixa = (indice: number, k: number, faixa: Faixa) =>
    mexer(indice, (d) => ({
      ...d,
      ranges: d.ranges.map((r, m) => (m === k ? faixa : r)),
    }));

  return (
    <div className="space-y-2">
      {semana.map((dia, i) => (
        <div key={dia.weekday} className="rounded-[4px] border border-line bg-carbon-900 p-3">
          <label className={`flex items-center gap-3 ${disabled ? "" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-nitro-500)]"
              checked={dia.aberto}
              disabled={disabled}
              onChange={(e) => mexer(i, (d) => ({ ...d, aberto: e.target.checked }))}
            />
            <span className="flex-1 font-medium capitalize">{weekday(dia.weekday)}</span>
            {!dia.aberto && <span className="text-sm text-steel-dim">{t("hours.closed")}</span>}
          </label>

          {dia.aberto && (
            <div className="mt-3 space-y-2 pl-7">
              {dia.ranges.map((faixa, k) => (
                <div key={k} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={faixa[0]}
                    disabled={disabled}
                    aria-label={t("hours.startOf", { dia: weekday(dia.weekday) })}
                    onChange={(e) => mexerFaixa(i, k, [e.target.value, faixa[1]])}
                    className="rounded-[4px] border border-line bg-carbon-800 px-2 py-1.5 text-steel disabled:opacity-60"
                  />
                  <span className="text-steel-dim">{t("hours.to")}</span>
                  <input
                    type="time"
                    value={faixa[1]}
                    disabled={disabled}
                    aria-label={t("hours.endOf", { dia: weekday(dia.weekday) })}
                    onChange={(e) => mexerFaixa(i, k, [faixa[0], e.target.value])}
                    className="rounded-[4px] border border-line bg-carbon-800 px-2 py-1.5 text-steel disabled:opacity-60"
                  />
                  {!disabled && dia.ranges.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        mexer(i, (d) => ({
                          ...d,
                          ranges: d.ranges.filter((_, m) => m !== k),
                        }))
                      }
                      className="text-sm text-steel-dim hover:text-red-400"
                    >
                      {t("hours.removeRange")}
                    </button>
                  )}
                </div>
              ))}
              {!disabled && dia.ranges.length < MAX_FAIXAS && (
                <button
                  type="button"
                  onClick={() => mexer(i, (d) => ({ ...d, ranges: [...d.ranges, TARDE] }))}
                  className="text-sm text-racing-500 hover:underline"
                >
                  {t("hours.addBreak")}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
